import React, { useContext } from 'react';
import { SignupContext } from './SignupContext';
import { phoneFormat } from '../../../api/utils/format/numbers';
import TextWinds from '../../../components/common/TextWinds';
import { useAppDispatch } from '../../../store/hook';
import { useNavigate } from 'react-router-dom';
import ErrorInput from '../../student/components/ErrorInput';
import { openConfirm } from '../../../store/features/modalSlice';
import { check14UnderAge, uidRegExp } from '../../../api/utils/util-func';
import GuadianForm from './GuadianForm';
import { Input } from 'antd';
import HFbutton from '../../../components/common/input/HFbutton';
import FormRow from '../../../components/common/input/FormRow';
import {
  SIGNUP_MESSAGES,
  SIGNUP_LABELS,
  SIGNUP_BUTTONS,
  SIGNUP_PLACEHOLDERS,
} from './constants';
import { useSignupForm } from './hooks/useSignupForm';
import { useUidDuplicateCheck } from './hooks/useUidDuplicateCheck';
import { useSignupSubmit } from './hooks/useSignupSubmit';
const InfoForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { phone } = useContext(SignupContext);

  // Custom hooks
  const {
    control,
    handleSubmit,
    watch,
    errors,
    password,
    uid,
    clearErrors,
  } = useSignupForm();
  const { checkDuplicate, getIdCheck } = useUidDuplicateCheck(
    uid,
    control,
    clearErrors,
  );
  const { isLoading, onSubmit } = useSignupSubmit(checkDuplicate, control);

  const handleCancel = () => {
    dispatch(
      openConfirm({
        message: SIGNUP_MESSAGES.CANCEL_CONFIRM,
        onConfirm: () => {
          navigate('/signin');
        },
        cancelText: SIGNUP_BUTTONS.CONTINUE_SIGNUP,
        confirmText: SIGNUP_BUTTONS.CANCEL_SIGNUP,
      }),
    );
  };

  return (
    <form className="flex flex-col gap-4">
      <section>
        <TextWinds type="title_h2">{SIGNUP_LABELS.TITLE}</TextWinds>
      </section>
      <section className="flex flex-col gap-2">
        <TextWinds type="caption1" children={SIGNUP_LABELS.PHONE} />
        <Input
          size="large"
          placeholder={SIGNUP_PLACEHOLDERS.PHONE}
          value={phoneFormat(phone)}
          disabled
        />

        <FormRow
          type="text-button"
          name="uid"
          className="p-3"
          rules={{
            required: SIGNUP_MESSAGES.UID_REQUIRED,
            minLength: {
              value: 5,
              message: SIGNUP_MESSAGES.UID_MIN_LENGTH,
            },
            maxLength: {
              value: 20,
              message: SIGNUP_MESSAGES.UID_MAX_LENGTH,
            },
            pattern: {
              value: uidRegExp,
              message: SIGNUP_MESSAGES.UID_PATTERN_DETAIL,
            },
            validate: () => {
              if (checkDuplicate) {
                return true;
              } else {
                return SIGNUP_MESSAGES.DUPLICATE_CHECK_REQUIRED;
              }
            },
          }}
          placeholder={SIGNUP_PLACEHOLDERS.UID}
          control={control}
          error={!!errors.uid}
          textButtonProp={{
            text: SIGNUP_BUTTONS.DUPLICATE_CHECK,
            action: getIdCheck,
          }}
        />
        {errors.uid && <ErrorInput text={errors.uid.message} />}

        <FormRow
          type="text"
          name="email"
          rules={{
            required: SIGNUP_MESSAGES.EMAIL_REQUIRED,
            pattern: {
              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
              message: SIGNUP_MESSAGES.EMAIL_PATTERN_INVALID,
            },
          }}
          placeholder={SIGNUP_PLACEHOLDERS.EMAIL}
          control={control}
          error={!!errors.email}
        />
        {errors.email && <ErrorInput text={errors.email.message} />}

        <FormRow
          type="password"
          name="password"
          rules={{
            required: SIGNUP_MESSAGES.PASSWORD_REQUIRED,
            pattern: {
              value:
                /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,20}$/,
              message: SIGNUP_MESSAGES.PASSWORD_PATTERN_INVALID,
            },
          }}
          placeholder={SIGNUP_PLACEHOLDERS.PASSWORD}
          control={control}
          error={!!errors.password}
        />
        {errors.password && <ErrorInput text={errors.password.message} />}

        <FormRow
          type="password"
          name="password2"
          rules={{
            required: SIGNUP_MESSAGES.PASSWORD_CONFIRM_REQUIRED,
            validate: (value) =>
              value === password || SIGNUP_MESSAGES.PASSWORD_NOT_MATCH,
          }}
          placeholder={SIGNUP_PLACEHOLDERS.PASSWORD_CONFIRM}
          control={control}
          error={!!errors.password2}
        />
        {errors.password2 && <ErrorInput text={errors.password2.message} />}

        <FormRow
          type="text"
          name="name"
          rules={{
            required: SIGNUP_MESSAGES.NAME_REQUIRED,
            pattern: {
              value: /^[가-힣]{2,20}$/,
              message: SIGNUP_MESSAGES.NAME_PATTERN_INVALID,
            },
          }}
          placeholder={SIGNUP_PLACEHOLDERS.NAME}
          control={control}
          error={!!errors.name}
        />
        {errors.name && <ErrorInput text={errors.name.message} />}

        <FormRow
          name={'birthday'}
          type={'date'}
          rules={{ required: SIGNUP_MESSAGES.BIRTHDAY_REQUIRED }}
          control={control}
          placeholder={SIGNUP_PLACEHOLDERS.BIRTHDAY}
          error={!!errors.birthday}
        />
        {errors.birthday && <ErrorInput text={errors.birthday.message} />}

        <FormRow
          name="gender"
          type={'gender'}
          control={control}
          rules={{ required: SIGNUP_MESSAGES.GENDER_REQUIRED }}
          error={!!errors.gender}
        />
        {errors.gender && <ErrorInput text={errors.gender.message} />}
      </section>
      {check14UnderAge(watch('birthday')) && (
        <section>
          <GuadianForm control={control} errors={errors} />
        </section>
      )}
      <section className="flex flex-col gap-2">
        <HFbutton onClick={handleSubmit(onSubmit)} disabled={isLoading}>
          {isLoading
            ? SIGNUP_BUTTONS.SIGNUP_IN_PROGRESS
            : SIGNUP_BUTTONS.SIGNUP}
        </HFbutton>
        <HFbutton type="Outlined" onClick={handleCancel}>
          {SIGNUP_BUTTONS.CANCEL}
        </HFbutton>
      </section>
    </form>
  );
};

export default InfoForm;
